"use client";

type EventPayload = { name: string; ts?: number; props?: Record<string, unknown> };

const queue: EventPayload[] = [];
let flushing = false;

function enqueue(ev: EventPayload) {
  queue.push({ ...ev, ts: ev.ts || Date.now() });
  if (!flushing) flushSoon();
}

function flushSoon() {
  if (flushing) return;
  flushing = true;
  const schedule = (cb: () => void, ms: number) => setTimeout(cb, ms);
  // Defer to idle when available to avoid competing with initial render
  type IdleCb = (deadline: { didTimeout: boolean; timeRemaining: () => number }) => void;
  type RequestIdle = (cb: IdleCb, opts?: { timeout?: number }) => number;
  const ric = (globalThis as any).requestIdleCallback as undefined | RequestIdle;
  if (typeof ric === 'function') {
    ric(() => flush(), { timeout: 1500 });
  } else {
    schedule(flush, 600);
  }
}

async function flush() {
  try {
    if (queue.length === 0) return;
    const batch = queue.splice(0, queue.length);
    const body = JSON.stringify({ events: batch });
    const url = '/api/analytics';
    if (navigator?.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } else {
      // Fire and forget; never block UI
      void fetch(url, { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true }).catch(() => {});
    }
  } catch {}
  finally { flushing = false; }
}

export function track(name: string, props?: Record<string, unknown>) {
  enqueue({ name, props });
}

export function trackPageView(path?: string) {
  try {
    const p = path || (typeof window !== 'undefined' ? window.location.pathname : undefined) || '/';
    enqueue({ name: 'page_view', props: { path: p } });
  } catch {}
}

// Lightweight event sender for error analytics with debounce per video
const lastSentMap = new Map<string, number>();

function buildKey(type: string, payload: Record<string, any>): string {
  const id = payload?.id != null ? String(payload.id) : '';
  const status = payload?.status != null ? String(payload.status) : '';
  const code = payload?.code != null ? String(payload.code) : '';
  return [type, id, status, code].join('|');
}

export function postAnalytics(type: string, payload: Record<string, any>) {
  try {
    const key = buildKey(type, payload || {});
    const now = Date.now();
    const last = lastSentMap.get(key) || 0;
    // Debounce identical error types per video for 60s
    if (now - last < 60_000) return;
    lastSentMap.set(key, now);
    const ev: EventPayload = { name: type, ts: now, props: payload || {} };
    const body = JSON.stringify({ events: [ev] });
    (async () => {
      const url = '/api/analytics';
      if (navigator?.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        // Fire & forget; never block UI
        void fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
      }
    })();
  } catch { /* swallow */ }
}
